import {
  Component,
  OnInit,
  signal,
  computed,
  inject,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';

import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { TooltipModule } from 'primeng/tooltip';

export const RESERVED_INVENTORY_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'spuCode',
    label: 'reserved_inventory_management.search_filter.product_code',
    type: 'text',
    placeholder: 'Please enter',
  },
  {
    key: 'spuName',
    label: 'reserved_inventory_management.search_filter.product_name',
    type: 'text',
    placeholder: 'Please enter',
  },
  {
    key: 'skuCode',
    label: 'reserved_inventory_management.search_filter.sku_code',
    type: 'text',
    placeholder: 'Please enter',
  },
  {
    key: 'warehouseCode',
    label: 'reserved_inventory_management.search_filter.warehouse_code',
    type: 'text',
    placeholder: 'Please enter',
  },
  {
    key: 'orderNumber',
    label: 'reserved_inventory_management.search_filter.order_number',
    type: 'text',
    placeholder: 'Please enter',
  },
  {
    key: 'status',
    label: 'reserved_inventory_management.search_filter.status',
    type: 'select',
    placeholder: 'Please select',
    options: [
      { label: 'reserved_inventory_management.status.all', value: 'all' },
      {
        label: 'reserved_inventory_management.status.reserved',
        value: 'reserved',
      },
      {
        label: 'reserved_inventory_management.status.released',
        value: 'released',
      },
      {
        label: 'reserved_inventory_management.status.pending',
        value: 'pending',
      },
      {
        label: 'reserved_inventory_management.status.over_reserved',
        value: 'over-reserved',
      },
    ],
  },
  {
    key: 'reservationDate',
    label: 'reserved_inventory_management.table.columns.reservation_date',
    type: 'dateRange',
    range: { startKey: 'startDate', endKey: 'endDate' },
  },
];

export const RESERVED_INVENTORY_TABLE_CONFIG: TableColumn[] = [
  {
    label: 'reserved_inventory_management.table.columns.product_code',
    prop: 'spuCode',
    width: 160,
  },
  {
    label: 'reserved_inventory_management.table.columns.product_name',
    prop: 'spuName',
    width: 160,
  },
  {
    label: 'reserved_inventory_management.table.columns.sku_code',
    prop: 'skuCode',
    width: 160,
  },
  {
    label: 'reserved_inventory_management.table.columns.warehouse_code',
    prop: 'warehouseCode',
    width: 160,
  },
  {
    label: 'reserved_inventory_management.table.columns.warehouse_name',
    prop: 'warehouseName',
    width: 160,
  },
  {
    label: 'reserved_inventory_management.table.columns.reserved_quantity',
    prop: 'reservedQuantity',
    width: 140,
    type: 'custom',
    styleClass: 'text-right',
  },
  {
    label: 'reserved_inventory_management.table.columns.available_quantity',
    prop: 'availableQuantity',
    width: 140,
    type: 'custom',
    styleClass: 'text-right',
  },
  {
    label: 'reserved_inventory_management.table.columns.order_number',
    prop: 'orderNumber',
    width: 160,
  },
  {
    label: 'reserved_inventory_management.table.columns.customer_name',
    prop: 'customerName',
    width: 140,
  },
  {
    label: 'reserved_inventory_management.table.columns.reservation_date',
    prop: 'reservationDate',
    width: 135,
    type: 'custom',
  },
  {
    label: 'reserved_inventory_management.table.columns.status',
    prop: 'status',
    width: 120,
    type: 'status',
  },
  {
    label: 'reserved_inventory_management.table.columns.action',
    prop: 'actions',
    type: 'actions',
    width: 180,
  },
];

export const RESERVED_INVENTORY_DEFAULT_CONFIG = {
  PAGE_SIZE: 20,
} as const;

export interface ReservedInventoryItem {
  id: number;
  spuCode: string;
  spuName: string;
  skuCode: string;
  warehouseCode: string;
  warehouseName: string;
  reservedQuantity: number;
  availableQuantity: number;
  orderNumber: string;
  customerName: string;
  reservationDate: string;
  status: 'Reserved' | 'Released' | 'Pending' | 'Over-reserved';
  isOverReserved: boolean;
  hasConflict: boolean;
}

@Component({
  selector: 'app-reserved-inventory',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    DataTableComponent,
    ConfirmationDialogComponent,
    TooltipModule,
  ],
  providers: [MessageService],
  templateUrl: './reserved-inventory.component.html',
  styleUrl: './reserved-inventory.component.css',
})
export class ReservedInventoryComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private destroy$ = new Subject<void>();

  // Configuration
  searchConfig = signal(RESERVED_INVENTORY_SEARCH_CONFIG);
  tableColumns = RESERVED_INVENTORY_TABLE_CONFIG;
  pageSize = RESERVED_INVENTORY_DEFAULT_CONFIG.PAGE_SIZE;

  // State management
  reservedItems = signal<ReservedInventoryItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  selectedItems = signal<ReservedInventoryItem[]>([]);
  first = signal(0);

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  selectedRecord: ReservedInventoryItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'release' | 'adjust' | null = null;

  ngOnInit(): void {
    this.loadData();
    this.loadOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    this.loadData(searchData);
  }

  onReset(): void {
    this.loadData();
  }

  onPageChange(event: any): void {
    this.loadData();
  }

  onSelectionChange(selectedItems: ReservedInventoryItem[]): void {
    this.selectedItems.set(selectedItems);
  }

  onViewDetails(record: ReservedInventoryItem): void {
    this.router.navigate(['/stock/reserved-inventory', record.id]);
  }

  onReleaseItem(record: ReservedInventoryItem): void {
    this.selectedRecord = record;
    this.dialogTitle = this.translateService.instant(
      'reserved_inventory_management.dialogs.release_reservation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'reserved_inventory_management.dialogs.release_reservation.message',
      { productCode: record.spuCode },
    );
    this.currentAction = 'release';
    this.dialogButtons = [
      {
        label: this.translateService.instant(
          'reserved_inventory_management.dialogs.release_reservation.buttons.cancel',
        ),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          'reserved_inventory_management.dialogs.release_reservation.buttons.release',
        ),
        action: 'confirm',
        severity: 'success',
      },
    ];
    this.showConfirmationDialog.set(true);
  }

  onAdjustQuantity(record: ReservedInventoryItem): void {
    this.selectedRecord = record;
    this.dialogTitle = this.translateService.instant(
      'reserved_inventory_management.dialogs.adjust_quantity.title',
    );
    this.dialogMessage = this.translateService.instant(
      'reserved_inventory_management.dialogs.adjust_quantity.message',
      { productCode: record.spuCode },
    );
    this.currentAction = 'adjust';
    this.dialogButtons = [
      {
        label: this.translateService.instant(
          'reserved_inventory_management.dialogs.adjust_quantity.buttons.cancel',
        ),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          'reserved_inventory_management.dialogs.adjust_quantity.buttons.adjust',
        ),
        action: 'confirm',
        severity: 'secondary',
      },
    ];
    this.showConfirmationDialog.set(true);
  }

  onEditQuantity(record: ReservedInventoryItem, newQuantity: number): void {
    // Update the quantity
    const updatedItems = this.reservedItems().map((item) =>
      item.id === record.id ? { ...item, reservedQuantity: newQuantity } : item,
    );
    this.reservedItems.set(updatedItems);

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant(
        'reserved_inventory_management.dialogs.messages.quantity_updated',
      ),
      detail: this.translateService.instant(
        'reserved_inventory_management.dialogs.messages.quantity_updated_detail',
        { productCode: record.spuCode },
      ),
    });
  }

  onConfirmatiButtonClick(action: string): void {
    if (action === 'confirm' && this.selectedRecord) {
      if (this.currentAction === 'release') {
        this.confirmRelease();
      } else if (this.currentAction === 'adjust') {
        this.confirmAdjust();
      }
    }
    this.closeConfirmationDialog();
  }

  private confirmRelease(): void {
    if (this.selectedRecord) {
      const updatedItems = this.reservedItems().map((item) =>
        item.id === this.selectedRecord!.id
          ? { ...item, status: 'Released' as const, reservedQuantity: 0 }
          : item,
      );
      this.reservedItems.set(updatedItems);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant(
          'reserved_inventory_management.dialogs.messages.reservation_released',
        ),
        detail: this.translateService.instant(
          'reserved_inventory_management.dialogs.messages.reservation_released_detail',
          { productCode: this.selectedRecord.spuCode },
        ),
      });
    }
  }

  private confirmAdjust(): void {
    if (this.selectedRecord) {
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant(
          'reserved_inventory_management.dialogs.messages.adjust_quantity',
        ),
        detail: this.translateService.instant(
          'reserved_inventory_management.dialogs.messages.adjust_quantity_detail',
          { productCode: this.selectedRecord.spuCode },
        ),
      });
    }
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog.set(false);
    this.selectedRecord = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  private loadData(searchParams?: SearchFormData): void {
    this.loading.set(true);

    // Mock data for now - replace with actual service call
    const mockItems: ReservedInventoryItem[] = [
      {
        id: 1,
        spuCode: 'SPU-001',
        spuName: 'Wireless Bluetooth Headphones',
        skuCode: 'SKU-001',
        warehouseCode: 'WH-001',
        warehouseName: 'Main Warehouse',
        reservedQuantity: 50,
        availableQuantity: 120,
        orderNumber: 'ORD-2024-001',
        customerName: 'John Smith',
        reservationDate: '2024-01-21',
        status: 'Reserved',
        isOverReserved: false,
        hasConflict: false,
      },
      {
        id: 2,
        spuCode: 'SPU-002',
        spuName: 'Smart Watch Series 8',
        skuCode: 'SKU-002',
        warehouseCode: 'WH-001',
        warehouseName: 'Main Warehouse',
        reservedQuantity: 25,
        availableQuantity: 15,
        orderNumber: 'ORD-2024-002',
        customerName: 'Sarah Johnson',
        reservationDate: '2024-01-21',
        status: 'Over-reserved',
        isOverReserved: true,
        hasConflict: true,
      },
      {
        id: 3,
        spuCode: 'SPU-003',
        spuName: 'Gaming Mechanical Keyboard',
        skuCode: 'SKU-003',
        warehouseCode: 'WH-002',
        warehouseName: 'Secondary Warehouse',
        reservedQuantity: 30,
        availableQuantity: 45,
        orderNumber: 'ORD-2024-003',
        customerName: 'Mike Wilson',
        reservationDate: '2024-01-20',
        status: 'Pending',
        isOverReserved: false,
        hasConflict: false,
      },
      {
        id: 4,
        spuCode: 'SPU-004',
        spuName: 'USB-C Hub 7-in-1',
        skuCode: 'SKU-004',
        warehouseCode: 'WH-003',
        warehouseName: 'Regional Warehouse',
        reservedQuantity: 0,
        availableQuantity: 80,
        orderNumber: 'ORD-2024-004',
        customerName: 'Lisa Brown',
        reservationDate: '2024-01-19',
        status: 'Released',
        isOverReserved: false,
        hasConflict: false,
      },
    ];

    setTimeout(() => {
      this.reservedItems.set(mockItems);
      this.totalRecords.set(mockItems.length);
      this.loading.set(false);
    }, 400);
  }

  private loadOptions(): void {
    // Translate status options
    const statusField = this.searchConfig().find(
      (field) => field.key === 'status',
    );
    if (statusField && statusField.options) {
      const translatedOptions = statusField.options.map((option) => ({
        ...option,
        label: this.translateService.instant(option.label),
      }));

      this.searchConfig.set(
        this.searchConfig().map((field) =>
          field.key === 'status'
            ? { ...field, options: translatedOptions }
            : field,
        ),
      );
    }
  }

  // Status methods for reservation status
  formatReservationStatus(status: string): string {
    switch (status) {
      case 'Reserved':
        return this.translateService.instant(
          'reserved_inventory_management.status.reserved',
        );
      case 'Released':
        return this.translateService.instant(
          'reserved_inventory_management.status.released',
        );
      case 'Pending':
        return this.translateService.instant(
          'reserved_inventory_management.status.pending',
        );
      case 'Over-reserved':
        return this.translateService.instant(
          'reserved_inventory_management.status.over_reserved',
        );
      default:
        return status;
    }
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Reserved':
        return 'info';
      case 'Released':
        return 'success';
      case 'Pending':
        return 'warn';
      case 'Over-reserved':
        return 'danger';
      default:
        return 'info';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Reserved':
        return 'status-badge reserved';
      case 'Released':
        return 'status-badge released';
      case 'Pending':
        return 'status-badge pending';
      case 'Over-reserved':
        return 'status-badge over-reserved';
      default:
        return 'status-badge';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
}
